import { memo, useState } from "react";
import { Cell, Pie, PieChart, Sector, Tooltip } from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { Stack } from "@/core/components/ui/stack";
import { formatMoney } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";

const MAX_LEGEND_ITEMS = 4;
const MAX_SLICES = 10;
const OTHER_ENTRY_NAME = "Other";

const SLICE_COLORS = Array.from(
  { length: MAX_SLICES },
  (_, index) => `hsl(var(--chart-${index + 1}))`,
);

const ActiveSlice = (props: PieSectorDataItem) => (
  <Sector {...props} outerRadius={(props.outerRadius ?? 0) + 6} />
);

const TooltipContent = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { name: string; value: number; payload: { fill: string } }[];
}) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: item } = payload[0];
  return (
    <div className="grid min-w-[14rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
          style={{ backgroundColor: item.fill }}
        />
        <div className="flex flex-1 justify-between gap-4">
          <span className="text-muted-foreground">{name}</span>
          <span className="font-mono font-medium tabular-nums text-foreground">
            {formatMoney(String(value))}
          </span>
        </div>
      </div>
    </div>
  );
};

export interface IcmPieChartEntry {
  name: string;
  value: number;
}

interface IcmPieChartProps {
  data: IcmPieChartEntry[];
}

const groupChartEntries = (data: IcmPieChartEntry[]) => {
  const sorted = [...data].sort((a, b) => b.value - a.value);

  if (sorted.length <= MAX_SLICES) return sorted;

  const visibleEntries = sorted.slice(0, MAX_SLICES - 1);
  const otherValue = sorted
    .slice(MAX_SLICES - 1)
    .reduce((sum, entry) => sum + entry.value, 0);

  return [
    ...visibleEntries,
    {
      name: OTHER_ENTRY_NAME,
      value: parseFloat(otherValue.toFixed(2)),
    },
  ].sort((a, b) => b.value - a.value);
};

const LegendItem = ({
  entry,
  color,
}: {
  entry: IcmPieChartEntry;
  color: string;
}) => (
  <div className="flex items-baseline gap-1.5">
    <div
      className="mt-[3px] h-2.5 w-2.5 shrink-0 self-start rounded-full"
      style={{ backgroundColor: color }}
    />
    <span className="text-xs text-secondary-text">
      {entry.name}{" "}
      <span className="font-semibold text-foreground">
        {formatMoney(String(entry.value))}
      </span>
    </span>
  </div>
);

export const IcmPieChart = memo(({ data }: IcmPieChartProps) => {
  const [showAll, setShowAll] = useState(false);

  if (data.length === 0) return null;

  const sorted = groupChartEntries(data);
  const visible = sorted.slice(0, MAX_LEGEND_ITEMS);
  const hasMore = sorted.length > MAX_LEGEND_ITEMS;

  return (
    <Stack direction="row" align="center" gap="lg">
      <div className="h-32 w-32 shrink-0">
        <PieChart width={128} height={128}>
          <Pie
            data={sorted}
            dataKey="value"
            nameKey="name"
            innerRadius={28}
            outerRadius={52}
            stroke="none"
            animationBegin={0}
            animationDuration={400}
            animationEasing="ease-out"
            activeShape={ActiveSlice}
          >
            {sorted.map((entry, i) => (
              <Cell
                key={entry.name}
                fill={SLICE_COLORS[i % SLICE_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<TooltipContent />} wrapperStyle={{ zIndex: 50 }} />
        </PieChart>
      </div>

      {/* Legend with inline expand */}
      <div className="relative">
        {/* Static items — layout anchor; fades out as the panel opens so dots don't peek */}
        <Stack
          gap="xs"
          className={cn("transition-opacity duration-150", {
            "opacity-0": showAll,
            "opacity-100": !showAll,
          })}
        >
          {visible.map((entry, i) => (
            <LegendItem
              key={entry.name}
              entry={entry}
              color={SLICE_COLORS[i % SLICE_COLORS.length]}
            />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-fit text-left text-xs text-muted-foreground hover:text-foreground"
            >
              Show all ({sorted.length})
            </button>
          )}
        </Stack>

        {/* Expanded panel: absolutely covers from item-1 downward */}
        {showAll && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowAll(false)}
            />
            <div className="absolute left-0 top-0 z-20 rounded-lg border bg-card py-2 pl-2 pr-8 shadow-lg">
              <Stack gap="xs">
                {sorted.map((entry, i) => (
                  <div
                    key={entry.name}
                    className={cn("animate-in fade-in", "duration-200", {
                      "zoom-in-95 slide-in-from-bottom-1": i < MAX_LEGEND_ITEMS,
                      "slide-in-from-top-3": i >= MAX_LEGEND_ITEMS,
                    })}
                    style={{
                      animationDelay:
                        i < MAX_LEGEND_ITEMS
                          ? `${i * 20}ms`
                          : `${(i - MAX_LEGEND_ITEMS) * 50}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <LegendItem
                      entry={entry}
                      color={SLICE_COLORS[i % SLICE_COLORS.length]}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setShowAll(false)}
                  className="mt-0.5 w-fit text-left text-xs text-muted-foreground hover:text-foreground"
                >
                  Show less
                </button>
              </Stack>
            </div>
          </>
        )}
      </div>
    </Stack>
  );
});
